import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import client from '../lib/api';
import { applySiteTheme, cacheSiteTheme } from '../lib/theme';
import { applyFavicon } from '../lib/favicon';
import { cacheBrand } from '../lib/brand';
import {
  fillSplash, hideSplash, isShellSplashPath, splashHoldMs, styleSpinlabSplash, SPINLAB_SPLASH_FALLBACK_MS,
} from '../lib/splash';
import { readSettingsPrefetch } from '../lib/settingsPrefetch';
import { getSiteTheme } from '../lib/siteThemes';
import realtime, { pollGate } from '../lib/realtime';
import { setProviderAliasEnabled } from '../lib/providerAlias';

const SettingsContext = createContext(null);
export const SETTINGS_UPDATED_EVENT = 'app:settings-updated';
const SETTINGS_UPDATED_STORAGE_KEY = 'app:settings-updated-at';

// Sunucu yeniden başlarken (kod güncellemesi, servis yenileme) ters vekil
// birkaç saniye 502/503/504 döner; ağ kopukluğunda ise yanıt hiç gelmez.
// Bu, uygulamanın döndürdüğü sıradan bir hatadan (401, 404, 422) ayrılmalıdır:
// orada ayarlar gelmiştir ve site çalışır, burada elde hiçbir şey yoktur ve
// site yarım açılırsa oyuncu "site bozuldu" der.
const UNREACHABLE_STATUSES = new Set([502, 503, 504, 521, 522, 523, 524]);

export const isServerUnreachable = (error) => {
  // İptal edilen istek (sayfa değişimi, StrictMode) kesinti değildir.
  if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') return false;
  const status = error?.response?.status;
  // Yanıt hiç gelmediyse (ağ hatası, zaman aşımı) sunucuya ulaşılamıyordur.
  if (status === undefined) return true;
  return UNREACHABLE_STATUSES.has(status);
};

// Alan adı kapısı (26.10.2026, backend services/domain_gate.py): Ortak Alan
// Adları'nda bekleyen (Sıradaki/Sonrakiler) ya da hiç kayıtlı olmayan adres
// cevap vermez. Sunucu 404 + `code: DOMAIN_NOT_FOUND` döner; site kabuğu yerine
// markasız "Alan adı bulunamadı" ekranı çizilir. Domain aktif edilince olağan
// 30 sn turu ayarları alır ve site kendiliğinden açılır.
export const DOMAIN_NOT_FOUND_CODE = 'DOMAIN_NOT_FOUND';

export const isDomainNotFound = (error) => {
  const data = error?.response?.data;
  return error?.response?.status === 404
    && (data?.code === DOMAIN_NOT_FOUND_CODE || data?.detail_data?.code === DOMAIN_NOT_FOUND_CODE);
};

// Kesinti sürerken ayarlar 30 saniyelik olağan turdan daha sık yoklanır:
// güncelleme genelde yarım dakikada biter ve oyuncu sayfayı elle yenilemek
// zorunda kalmamalıdır.
export const UNREACHABLE_RETRY_MS = 3000;

export const notifySettingsUpdated = () => {
  window.dispatchEvent(new Event(SETTINGS_UPDATED_EVENT));
  try {
    localStorage.setItem(SETTINGS_UPDATED_STORAGE_KEY, String(Date.now()));
  } catch {
    // Bazı gizli/katı tarayıcı oturumlarında localStorage kullanılamayabilir.
  }
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [unreachable, setUnreachable] = useState(false);
  const [domainMissing, setDomainMissing] = useState(false);

  const refreshSettings = useCallback(async () => {
    // İlk tur, index.html'in paketten önce başlattığı isteği kullanır; aynı
    // cevabı ikinci kez istemek telefonda açılışı uzatırdı. Başarısızsa
    // olağan isteğe düşülür (bkz. lib/settingsPrefetch.js).
    const prefetched = await readSettingsPrefetch();
    if (prefetched) {
      setProviderAliasEnabled(prefetched.provider_alias);
      setSettings(prefetched);
      setUnreachable(false);
      setDomainMissing(false);
      return prefetched;
    }
    try {
      const { data } = await client.get('/settings', {
        params: { _: Date.now() },
        headers: { 'Cache-Control': 'no-cache' },
      });
      setProviderAliasEnabled(data?.provider_alias);
      setSettings(data);
      setUnreachable(false);
      setDomainMissing(false);
      return data;
    } catch (error) {
      setDomainMissing(isDomainNotFound(error));
      setUnreachable(isServerUnreachable(error));
      setSettings((current) => current || {});
      return null;
    }
  }, []);

  useEffect(() => {
    refreshSettings();
    const handleSettingsUpdated = () => refreshSettings();
    const handleStorage = (event) => {
      if (event.key === SETTINGS_UPDATED_STORAGE_KEY) refreshSettings();
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refreshSettings();
    };
    window.addEventListener(SETTINGS_UPDATED_EVENT, handleSettingsUpdated);
    window.addEventListener('storage', handleStorage);
    window.addEventListener('focus', handleSettingsUpdated);
    document.addEventListener('visibilitychange', handleVisibility);
    // Gerçek zamanlı kanal canlıyken ayar değişikliği `settings` olayıyla
    // gelir; zamanlayıcı yalnız güvenlik ağıdır (5 dk). Misafir (kanal yok) 30 sn.
    const gate = pollGate(300000);
    const interval = window.setInterval(() => { if (!gate.skip()) refreshSettings(); }, 30000);
    const offSettings = realtime.on('settings', () => { gate.touch(); refreshSettings(); });
    return () => {
      offSettings();
      window.removeEventListener(SETTINGS_UPDATED_EVENT, handleSettingsUpdated);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', handleSettingsUpdated);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.clearInterval(interval);
    };
  }, [refreshSettings]);

  // Kesintide daha sık yoklanır ve sunucu döndüğü anda ekran kendiliğinden
  // devam eder; oyuncudan sayfayı yenilemesi istenmez.
  useEffect(() => {
    if (!unreachable) return undefined;
    const timer = window.setInterval(refreshSettings, UNREACHABLE_RETRY_MS);
    return () => window.clearInterval(timer);
  }, [unreachable, refreshSettings]);

  useEffect(() => {
    if (!settings) return;
    applySiteTheme(settings.primary_color, settings.secondary_color, settings.site_theme);
    cacheSiteTheme(settings.primary_color, settings.secondary_color, settings.site_theme);
    // Marka, ilk boyamada kullanılabilsin diye saklanır (bkz. lib/brand.js);
    // panelden boşaltılırsa önbellek de temizlenir.
    // Önbellek boşken (ilk ziyaret, yeni alan adı) açılış ekranı markasız
    // kalmış olabilir; ekran söndürülmeden önce doldurulur (bkz. lib/splash.js).
    const brand = cacheBrand(settings);
    const themeId = getSiteTheme(settings.site_theme).id;
    // Spinlab kendi açılışını gösterir (Winbino logosu + kaynak yükleme
    // halkası); ortak marka logosu ile doldurulmaz.
    if (themeId === 'spinlab') styleSpinlabSplash();
    else fillSplash(brand);
    if (!window.location.pathname.startsWith('/sayfa/')) {
      // Spinlab sekme başlığına kendi marka adını yazar (SpinlabShell).
      if (themeId !== 'spinlab') document.title = settings.site_name || '';
    }
    // Spinlab sekme simgesi kendi kare logosudur (SpinlabShell).
    if (themeId !== 'spinlab') applyFavicon(settings.favicon_url);
    // Tema, marka ve simge yerine oturdu: açılış ekranı artık kalkabilir.
    // Ayarlar hiç gelmese de (`refreshSettings` hata yolunda `{}` yazar) ekran
    // açılır; oyuncuyu sonsuza dek logoya bakarken bırakmak en kötüsüdür.
    // Bazı şemalarda ekran hazır olduktan sonra bir süre daha kalır
    // (bkz. lib/splash.js).
    // Spinlab kabuğu ekranı kendisi kaldırır (boyandığında); burada yalnızca
    // kabuk hiç gelmezse diye güvenlik süresi kurulur.
    if (themeId === 'spinlab' && isShellSplashPath()) {
      const timer = window.setTimeout(() => hideSplash(), SPINLAB_SPLASH_FALLBACK_MS);
      return () => window.clearTimeout(timer);
    }
    hideSplash({ holdMs: splashHoldMs(themeId) });
    return undefined;
  }, [settings]);

  // Elde hiç ayar yokken yaşanan kesinti siteyi kullanılamaz kılar; ayarlar
  // bir kez geldikten sonraki kısa kopukluk ise oyuncunun okuduğu sayfayı
  // kaplamamalıdır (o sırada yapılan istek zaten kendi hatasını gösterir).
  const hasSettings = settings !== null && Object.keys(settings).length > 0;

  return (
    <SettingsContext.Provider value={{
      ...(settings || {}),
      settingsLoaded: settings !== null,
      serverUnreachable: unreachable && !hasSettings,
      domainNotFound: domainMissing && !hasSettings,
      refreshSettings,
    }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext) || {};

/**
 * Bir alt ağaç için ayarların bazı alanlarını örter (kayıt değişmez). Spinlab
 * kabuğu kendi marka adını ve logosunu böyle verir: kabuğun içinde çizilen
 * ortak sayfalar (bonuslar, oyun penceresi, hesap) `site_name`'i ve logoyu
 * okuyarak sitenin ortak markasını (diğer şemaların) göstermesin.
 */
export const SettingsOverride = ({ values, children }) => {
  const parent = useContext(SettingsContext);
  const merged = React.useMemo(() => ({ ...(parent || {}), ...values }), [parent, values]);
  return <SettingsContext.Provider value={merged}>{children}</SettingsContext.Provider>;
};
